import HtmlWrapper from 'react-html-map';
import { getFormattedDate } from '#pie/i18n/dateFormats';
import type { CommentDto } from '#pie/internal/api/pie-bff';
import { createComment } from '#pie/internal/api/pie-bff/mocks';
import { parseDates } from '#pie/utils/date';
import { tv } from '#pie/utils/TailwindUtils';
import { AttachmentAnchor } from '../attachment-anchor/AttachmentAnchor';
import { Skeleton } from '../skeleton/Skeleton';
import { SkeletonLines } from '../skeleton-lines/SkeletonLines';
import { Text } from '../text/Text';

type CommentProps =
  | {
      comment: CommentDto;
      isLoading?: undefined;
    }
  | { comment?: undefined; isLoading: true };

export const Comment: React.FC<CommentProps> = ({ comment, isLoading }) => {
  const s = commentStyles();
  const { attachments, description, createdOn, createdBy } = parseDates(
    isLoading ? createComment() : comment,
    ['createdOn'],
  );

  const resolvedInitials = createdBy?.label
    ? (() => {
        const words = createdBy.label.trim().split(/\s+/).filter(Boolean);
        const first = words[0]?.charAt(0) ?? '';
        const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
        return (first + last).toUpperCase();
      })()
    : '';

  return (
    <div className={s.root()}>
      <div className={s.avatar()}>{isLoading ? null : resolvedInitials}</div>
      <div className={s.body()}>
        <div className={s.header()}>
          {isLoading ? (
            <Skeleton className="w-50" />
          ) : (
            <>
              <Text>{createdBy.label}</Text>
              <p className={s.date()}>{getFormattedDate(createdOn)}</p>
            </>
          )}
        </div>
        {description && (
          <div className={s.description()}>
            {isLoading ? (
              <>
                <SkeletonLines lines={2} />
                <Skeleton className="w-[50%]" />
              </>
            ) : (
              <HtmlWrapper decodeEntities html={description}>
                {{
                  a: ({ children, href, id, target }) => {
                    return (
                      <a
                        id={id}
                        rel="noopener noreferrer"
                        href={href}
                        target={target}
                        className="text-action-primary-enabled link"
                      >
                        {children}
                      </a>
                    );
                  },
                  b: ({ children }) => <strong>{children}</strong>,
                  br: () => <br />,
                  div: ({ children }) => <span>{children}</span>,
                  em: ({ children }) => <em>{children}</em>,
                  figure: ({ children }) => <figure>{children}</figure>,
                  i: ({ children }) => <em>{children}</em>,
                  li: ({ children }) => <li>{children}</li>,
                  ol: ({ children }) => (
                    <ol className="mb-4 ml-4 list-decimal">{children}</ol>
                  ),
                  p: ({ children }) => <p>{children}</p>,
                  span: ({ children }) => <>{children}</>,
                  strong: ({ children }) => <strong>{children}</strong>,
                  table: ({ children }) => (
                    <table className="bg-white">{children}</table>
                  ),
                  tbody: ({ children }) => (
                    <tbody className="border border-border-minimal-enabled">
                      {children}
                    </tbody>
                  ),
                  td: ({ children }) => (
                    <td className="border border-border-minimal-enabled px-2 py-1">
                      {children}
                    </td>
                  ),
                  thead: ({ children }) => <thead>{children}</thead>,
                  tr: ({ children }) => <tr>{children}</tr>,
                  u: ({ children }) => <u>{children}</u>,
                  ul: ({ children }) => (
                    <ul className="mb-4 ml-4 list-disc">{children}</ul>
                  ),
                }}
              </HtmlWrapper>
            )}
          </div>
        )}
        {!isLoading &&
          attachments.map((attachment) => (
            <AttachmentAnchor key={attachment.id} attachment={attachment} />
          ))}
      </div>
    </div>
  );
};

export const commentStyles = tv({
  slots: {
    root: 'flex flex-row gap-3 py-2',
    avatar:
      'text-xs text-content-reversed bg-surface-bold-gray h-6 w-6 grid place-items-center rounded-full',
    body: 'flex flex-col gap-3',
    header: 'flex flex-row gap-5 items-center',
    date: 'text-sm text-content-secondary',
    description: 'whitespace-break-spaces',
  },
});
